import React, { useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import confetti from 'canvas-confetti';
import OctaAvatar from './OctaAvatar';
import { avatarBg, CrownIcon, TrophyIcon, CloseIcon } from './LeaderboardParts';
import { HIDE_MONEY } from '../utils/platform';
import '../styles/app/leaderboard.css';

function VictoryModal({
  event,
  standings,
  onClose,
  poolName,
  entryFee = 0,
  memberCount = 0,
  payoutFirst = 60,
  payoutSecond = 30,
  payoutThird = 10,
  nextEvent = null,
  isGlobal = false
}) {
  const fireConfetti = useCallback(() => {
    const colors = ['#ffc857', '#ffe08a', '#ff2e2a', '#ffffff', '#3d7bff'];

    // Continuous side confetti
    const duration = 3000;
    const end = Date.now() + duration;

    (function frame() {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.7 },
        colors: colors,
        zIndex: 10001
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.7 },
        colors: colors,
        zIndex: 10001
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    }());

    // Big burst in the middle
    setTimeout(() => {
      confetti({
        particleCount: 100,
        spread: 100,
        origin: { x: 0.5, y: 0.5 },
        colors: colors,
        zIndex: 10001
      });
    }, 300);
  }, []);

  useEffect(() => {
    fireConfetti();
  }, [fireConfetti]);

  // Group standings by rank to handle ties
  const getWinnersByRank = () => {
    if (!standings || standings.length === 0) return { first: [], second: [], third: [] };

    const first = standings.filter(s => s.rank === 1);
    const second = standings.filter(s => s.rank === 2);
    const third = standings.filter(s => s.rank === 3);

    return { first, second, third };
  };

  const { first, second, third } = getWinnersByRank();

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Helper to get points display (handles both 'points' and 'total_points' fields)
  const getPoints = (entry) => entry?.points || entry?.total_points || 0;

  // Native shows the champion celebration without any prize amounts: a zero
  // pot hides the per-spot payouts and the Total Prize Pool panel.
  const totalPot = HIDE_MONEY ? 0 : entryFee * memberCount;

  // Calculate payouts with tie splitting
  const calculatePayout = (rank, tiedCount) => {
    if (totalPot === 0) return 0;
    const payouts = [payoutFirst, payoutSecond, payoutThird];
    let totalPct = 0;

    if (rank === 1) {
      for (let i = 0; i < Math.min(tiedCount, 3); i++) {
        totalPct += payouts[i];
      }
    } else if (rank === 2) {
      for (let i = 1; i < Math.min(1 + tiedCount, 3); i++) {
        totalPct += payouts[i];
      }
    } else if (rank === 3) {
      totalPct = payouts[2];
    }

    return Math.round((totalPot * totalPct / 100) / tiedCount);
  };

  const firstPayout = first.length > 0 ? calculatePayout(1, first.length) : 0;
  const secondPayout = second.length > 0 ? calculatePayout(2, second.length) : 0;
  const thirdPayout = third.length > 0 ? calculatePayout(3, third.length) : 0;

  const renderSpot = (group, place, payout) => {
    if (group.length === 0) return null;
    const label = place === 1
      ? (group.length > 1 ? 'CO-CHAMPS' : 'CHAMPION')
      : place === 2
        ? (group.length > 1 ? 'TIED 2ND' : '2ND PLACE')
        : (group.length > 1 ? 'TIED 3RD' : '3RD PLACE');
    return (
      <div className={`lb-spot is-${place}`}>
        {place === 1 && <CrownIcon className="lb-spot-crown" />}
        <div className="lb-spot-avatar">
          <OctaAvatar name={group[0].username} size={place === 1 ? 58 : 44} bg={avatarBg(group[0].user_id)} />
          <span className="lb-spot-medal">{place}</span>
        </div>
        <p className="lb-spot-place">{label}</p>
        <p className="lb-spot-name">{group.map(s => s.username).join(' & ')}</p>
        <p className="lb-spot-pts"><b>{getPoints(group[0])}</b><span>PTS</span></p>
        {totalPot > 0 && (
          <p className="lb-spot-payout">${payout}</p>
        )}
        <div className="lb-spot-block" aria-hidden="true"><span>{place}</span></div>
      </div>
    );
  };

  // Portal to <body> so the sheet sits above the app shell (dock/sidebar)
  // instead of inside the page's stacking context.
  return ReactDOM.createPortal(
    <div className="lb-victory-backdrop" onClick={onClose}>
      <div
        className="lb-victory"
        role="dialog"
        aria-modal="true"
        aria-label={`${event?.name || 'Event'} results`}
        onClick={e => e.stopPropagation()}
      >
        <div className="lb-victory-rays" aria-hidden="true" />

        {/* Always-visible close X (sticks to top-right inside the scrollable card) */}
        <div className="lb-victory-close-wrap">
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="lb-victory-close"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Header */}
        <div className="lb-victory-head">
          {isGlobal ? (
            <img className="lb-victory-belt" src="/images/title-belt.svg" alt="Title Belt" title="PNP Title Shot Winner" />
          ) : (
            <div className="lb-victory-badge"><TrophyIcon /></div>
          )}
          {poolName && (
            <p className="pnp-eyebrow pnp-eyebrow-gold pnp-eyebrow-plain">
              {poolName}
            </p>
          )}
          <h2 className="lb-victory-title">
            {event?.name} Results
          </h2>
          <p className="lb-victory-date">
            {event?.date && formatDate(event.date)}
          </p>
        </div>

        {/* Champion reveal */}
        {first.length > 0 && (
          <div className="lb-victory-champ">
            <span className="lb-victory-kicker">{first.length > 1 ? 'Co-Champs' : 'Champion'}</span>
            <div className="lb-victory-winner">{first.map(s => s.username).join(' & ')}</div>
          </div>
        )}

        {/* Podium */}
        {first.length === 0 && second.length === 0 && third.length === 0 ? (
          <div className="lb-victory-pending">
            <p>Results are being calculated...</p>
          </div>
        ) : (
          <div className="lb-podium">
            {/* 2nd Place - Left */}
            {second.length > 0 ? renderSpot(second, 2, secondPayout) : <div />}
            {/* 1st Place - Center */}
            {first.length > 0 ? renderSpot(first, 1, firstPayout) : <div />}
            {/* 3rd Place - Right */}
            {third.length > 0 ? renderSpot(third, 3, thirdPayout) : <div />}
          </div>
        )}

        {/* Prize Pool Info */}
        {isGlobal ? (
          <div className="lb-victory-panel is-gold">
            <img src="/images/title-belt.svg" alt="Belt" title="PNP Title Shot Winner" />
            <div>
              <p className="lb-victory-panel-title">
                Title Belt Awarded
              </p>
              <p className="lb-victory-panel-sub">
                Winner earns the PNP Title Shot belt badge
              </p>
            </div>
          </div>
        ) : totalPot > 0 ? (
          <div className="lb-victory-panel is-green">
            <p className="pnp-eyebrow pnp-eyebrow-mute">
              Total Prize Pool
            </p>
            <p className="lb-victory-pot">
              ${totalPot}
            </p>
          </div>
        ) : null}

        {/* Next Event Info */}
        {nextEvent && (
          <div className="lb-victory-panel is-blue">
            <div>
              <p className="pnp-eyebrow pnp-eyebrow-mute pnp-eyebrow-plain">
                Next Event
              </p>
              <p className="lb-victory-panel-title">
                {nextEvent.name}
              </p>
              {nextEvent.picks_open_at && (
                <p className="lb-victory-panel-sub">
                  Picks open {new Date(nextEvent.picks_open_at).toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    timeZoneName: 'short'
                  })}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Close Button */}
        <button
          className="btn btn-gold btn-lg btn-block lb-victory-cta"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>,
    document.body
  );
}

export default VictoryModal;
